import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { GameState, HoleInfo, HoleScore, Player, Team, BetType } from '@/lib/golfEngine';
import { playerReceivesStrokeOnHole, getStrokesOnHole, getFrontHoles, getBackHoles, getCompletedHolesInOrder, getLastPlayedHole } from '@/lib/golfEngine';
import { getGameEngine, isPressCapable } from '@/games/index';
import { computeConsolidatedPressGroups } from '@/lib/pressGroups';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { X, ChevronLeft, ChevronRight, Minus, Plus, Eraser, RotateCcw, Lock, Zap, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DT, dtScoreColor } from '@/lib/darkTheme';

export interface BulkHoleScores {
  holeNumber: number;
  scores: Record<string, number>;
}

interface HoleDetailModalProps {
  gameState: GameState;
  holeNumber: number;
  onClose: () => void;
  onBulkScoreChange: (payload: BulkHoleScores) => void;
  onHoleChange: (holeNumber: number) => void;
  onClearHole?: (holeNumber: number) => void;
  onCorrectEarlierHole?: () => void;
  onRequestPress?: (betType: BetType, side: 'top' | 'bottom', count: number, holeOverride?: number) => void;
  isCommish?: boolean;
}

type DensityMode = 'comfortable' | 'compact' | 'dense';

const DENSITY_ORDER: DensityMode[] = ['comfortable', 'compact', 'dense'];

function getInitialDensity(playerCount: number): DensityMode {
  if (playerCount <= 2) return 'comfortable';
  if (playerCount <= 4) return 'compact';
  return 'dense';
}

function nextDensity(current: DensityMode): DensityMode | null {
  const idx = DENSITY_ORDER.indexOf(current);
  return idx < DENSITY_ORDER.length - 1 ? DENSITY_ORDER[idx + 1] : null;
}

function useAdaptiveDensity(initialDensity: DensityMode) {
  const [density, setDensity] = useState(initialDensity);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const measuredRef = useRef(false);

  useEffect(() => {
    setDensity(initialDensity);
    measuredRef.current = false;
  }, [initialDensity]);

  const checkOverflow = useCallback(() => {
    if (!contentRef.current || !containerRef.current || measuredRef.current) return;
    const containerHeight = containerRef.current.clientHeight;
    const contentHeight = contentRef.current.scrollHeight;
    if (contentHeight > containerHeight) {
      setDensity(prev => {
        const next = nextDensity(prev);
        if (next) return next;
        measuredRef.current = true;
        return prev;
      });
    } else {
      measuredRef.current = true;
    }
  }, []);

  useEffect(() => {
    measuredRef.current = false;
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(checkOverflow);
    return () => cancelAnimationFrame(raf);
  }, [density, checkOverflow]);

  return { density, contentRef, containerRef };
}

const densityConfig = {
  comfortable: {
    rowPadding: 'px-2.5 py-2',
    rowGap: 'space-y-1.5',
    teamGap: 'space-y-2',
    nameFontSize: 'text-sm',
    labelFontSize: 'text-xs',
    scoreFontSize: 'text-xl',
    scoreBoxSize: 'min-w-[52px] min-h-[52px]',
    stepperSize: 'min-w-[44px] min-h-[44px]',
    stepperIconSize: 'w-5 h-5',
    stepperGap: 'gap-2.5',
    nameMarginBottom: 'mb-1.5',
    strokeDotSize: 'w-2 h-2',
    bodyPadding: 'px-3 py-2',
  },
  compact: {
    rowPadding: 'px-2.5 py-2',
    rowGap: 'space-y-1.5',
    teamGap: 'space-y-2',
    nameFontSize: 'text-sm',
    labelFontSize: 'text-xs',
    scoreFontSize: 'text-xl',
    scoreBoxSize: 'min-w-[48px] min-h-[48px]',
    stepperSize: 'min-w-[42px] min-h-[42px]',
    stepperIconSize: 'w-5 h-5',
    stepperGap: 'gap-2',
    nameMarginBottom: 'mb-1.5',
    strokeDotSize: 'w-2 h-2',
    bodyPadding: 'px-2.5 py-2',
  },
  dense: {
    rowPadding: 'px-2 py-1.5',
    rowGap: 'space-y-1',
    teamGap: 'space-y-1.5',
    nameFontSize: 'text-xs',
    labelFontSize: 'text-[11px]',
    scoreFontSize: 'text-lg',
    scoreBoxSize: 'min-w-[40px] min-h-[40px]',
    stepperSize: 'min-w-[36px] min-h-[36px]',
    stepperIconSize: 'w-4 h-4',
    stepperGap: 'gap-1.5',
    nameMarginBottom: 'mb-1',
    strokeDotSize: 'w-1.5 h-1.5',
    bodyPadding: 'px-2 py-1.5',
  },
} as const;

function getScoreLabel(score: number, par: number): string {
  const diff = score - par;
  if (diff <= -2) return 'Eagle';
  if (diff === -1) return 'Birdie';
  if (diff === 0) return 'Par';
  if (diff === 1) return 'Bogey';
  if (diff === 2) return 'Dbl Bogey';
  return `+${diff}`;
}

function getScoreColorHex(score: number | null, par: number): string {
  if (score === null) return DT.primaryText;
  const diff = score - par;
  if (diff <= -2) return DT.gold;
  if (diff === -1) return DT.winning;
  if (diff === 0) return DT.primaryText;
  if (diff === 1) return DT.losing;
  return DT.doubleBogey;
}

function formatTeamName(team: Team): string {
  if (team.players.length === 1) {
    const name = team.players[0]?.name ?? 'Player';
    return name.length > 8 ? name.substring(0, 8) : name;
  }
  return team.players.map(p => (p.name || '').substring(0, 5)).join('/');
}

function ScoreInputRow({ player, hole, displayScore, onLocalChange, isTeam1, isFinalized, density }: {
  player: Player;
  hole: HoleInfo;
  displayScore: number;
  onLocalChange: (score: number) => void;
  isTeam1: boolean;
  isFinalized: boolean;
  density: DensityMode;
}) {
  const strokeCount = getStrokesOnHole(player, hole);
  const receivesStroke = playerReceivesStrokeOnHole(player, hole);
  const netScore = receivesStroke ? displayScore - 1 : displayScore;
  const teamBg = isTeam1 ? DT.team1Bg : DT.team2Bg;
  const cfg = densityConfig[density];
  const scoreHex = getScoreColorHex(displayScore, hole.par);

  return (
    <div className={cn('rounded-lg', cfg.rowPadding)} style={{ background: teamBg, border: `1px solid ${DT.divider}` }} data-testid={`modal-score-row-${player.id}`}>
      <div className={cn('flex items-center justify-between', cfg.nameMarginBottom)}>
        <div className="flex items-center gap-1.5">
          <span style={{ color: DT.primaryText, fontWeight: 700, fontSize: '16px' }}>{player.name}</span>
          {strokeCount > 0 && (
            <span className="flex items-center gap-[2px]">
              {Array.from({ length: Math.min(strokeCount, 3) }, (_, i) => (
                <span key={i} className={cn('rounded-full', cfg.strokeDotSize)} style={{ background: DT.gold }} />
              ))}
              {strokeCount > 3 && (
                <span className="ml-0.5" style={{ fontSize: '11px', color: DT.primaryText, opacity: 0.5 }}>+{strokeCount - 3}</span>
              )}
            </span>
          )}
        </div>
        <span className={cfg.labelFontSize} style={{ color: scoreHex, fontWeight: 700 }}>
          {getScoreLabel(displayScore, hole.par)}
          {receivesStroke && (
            <span className="ml-1" style={{ color: DT.primaryText, opacity: 0.5 }}>(net {netScore})</span>
          )}
        </span>
      </div>
      <div className={cn('flex items-center justify-center', cfg.stepperGap)}>
        <button
          type="button"
          className={cn(
            'rounded-full flex items-center justify-center transition-colors',
            cfg.stepperSize,
            (isFinalized || displayScore <= 1) && 'opacity-30 pointer-events-none'
          )}
          style={{ border: `1px solid ${DT.divider}`, background: DT.surface, color: DT.primaryText }}
          disabled={isFinalized || displayScore <= 1}
          onClick={() => { if (displayScore > 1) onLocalChange(displayScore - 1); }}
          data-testid={`modal-btn-minus-${player.id}`}
        >
          <Minus className={cfg.stepperIconSize} />
        </button>
        <div
          className={cn(
            'flex items-center justify-center rounded-xl font-bold',
            cfg.scoreBoxSize,
            cfg.scoreFontSize,
          )}
          style={{ border: `2px solid ${DT.divider}`, background: DT.surface, color: scoreHex }}
          data-testid={`modal-score-display-${player.id}`}
        >
          {displayScore}
        </div>
        <button
          type="button"
          className={cn(
            'rounded-full flex items-center justify-center transition-colors',
            cfg.stepperSize,
            (isFinalized || displayScore >= 15) && 'opacity-30 pointer-events-none'
          )}
          style={{ border: `1px solid ${DT.divider}`, background: DT.surface, color: DT.primaryText }}
          disabled={isFinalized || displayScore >= 15}
          onClick={() => { if (displayScore < 15) onLocalChange(displayScore + 1); }}
          data-testid={`modal-btn-plus-${player.id}`}
        >
          <Plus className={cfg.stepperIconSize} />
        </button>
      </div>
    </div>
  );
}

function getAllPlayers(gameState: GameState): Player[] {
  return gameState.teams.flatMap(t => t.players);
}

function buildLocalScores(gameState: GameState, holeNumber: number, hole: HoleInfo): Record<string, number> {
  const local: Record<string, number> = {};
  for (const player of getAllPlayers(gameState)) {
    const existing = gameState.scores.find(
      s => s.playerId === player.id && s.holeNumber === holeNumber
    );
    local[player.id] = existing?.grossScore ?? hole.par;
  }
  return local;
}

function formatDollar(amount: number): string {
  const abs = Math.abs(amount);
  const str = abs % 1 === 0 ? `$${abs}` : `$${abs.toFixed(2)}`;
  if (amount > 0) return `+${str}`;
  if (amount < 0) return `-${str}`;
  return '$0';
}

function dollarInlineColor(amount: number): string {
  if (amount > 0) return DT.winning;
  if (amount < 0) return DT.losing;
  return DT.primaryText;
}

// Groups press options by betType (not by betType+side) so the carousel
// renders ONE button per eligible bet type — matching the Bet Status "Presses"
// section in PressRows.tsx. When both sides of the same bet type are
// simultaneously eligible (e.g. both teams can press Back at the catch-up
// point), that becomes one dropdown with two team-labeled items rather than
// two separate "Press Back" buttons.
interface CarouselBetGroup {
  betType: BetType;
  sides: Array<{ side: 'top' | 'bottom'; holeOverride?: number }>;
}

function getCarouselBetGroups(gameState: GameState, holeNumber: number): CarouselBetGroup[] {
  if (gameState.gameMode !== 'OnDemandPress' || gameState.isFinalized) return [];

  // computeConsolidatedPressGroups is the single source of truth — the same
  // function used by PressRows.tsx. Calling it here unmodified means the
  // carousel's eligible set is always identical to Bet Status, and Front/Back
  // mutual exclusion (enforced inside that function by selectedIsInFront /
  // selectedIsInBack flags) cannot be violated at the carousel level.
  const groups = computeConsolidatedPressGroups(gameState, holeNumber);

  // Carousel only handles fresh (main) presses, not press-on-press stacks —
  // intentional, matching the original carousel scope.
  const mainGroups = groups.filter(g => g.mainPressMax > 0);

  // Group by betType: one button per bet type; sides become dropdown items.
  const byType = new Map<BetType, CarouselBetGroup>();
  for (const g of mainGroups) {
    if (!byType.has(g.betType)) byType.set(g.betType, { betType: g.betType, sides: [] });
    byType.get(g.betType)!.sides.push({ side: g.side, holeOverride: g.holeOverride });
  }
  return Array.from(byType.values());
}

function CarouselDollarStatus({ gameState, holeNumber, onRequestPress, isCommish }: {
  gameState: GameState;
  holeNumber: number;
  onRequestPress?: (betType: BetType, side: 'top' | 'bottom', count: number, holeOverride?: number) => void;
  isCommish?: boolean;
}) {
  const engine = getGameEngine(gameState.gameType);
  const settlement = engine.settleGame(gameState);

  const frontHoles = getFrontHoles(gameState.startingHole);
  const isOnFront = frontHoles.includes(holeNumber);

  const frontAmount = settlement.perLeg.Front.amountForA;
  const backAmount = settlement.perLeg.Back.amountForA;
  const overallAmount = settlement.perLeg.Overall.amountForA;

  const betGroups = isCommish && onRequestPress ? getCarouselBetGroups(gameState, holeNumber) : [];

  // Renders one button per betType group.
  // Single side → plain button "Press [Type]"
  // Multiple sides → dropdown; each item is team-labeled to disambiguate.
  function renderPressControl(group: CarouselBetGroup) {
    const typeLabel = group.betType === 'Front' ? 'Front' : group.betType === 'Back' ? 'Back' : 'Overall';
    if (group.sides.length === 1) {
      const { side, holeOverride } = group.sides[0];
      return (
        <Button
          key={group.betType}
          size="sm"
          className="h-9 px-3 text-sm font-bold border-0 gap-1"
          style={{ background: DT.interactive, color: DT.darkBg }}
          onClick={() => onRequestPress!(group.betType, side, 1, holeOverride)}
          data-testid={`carousel-press-btn-${group.betType.toLowerCase()}`}
        >
          <Zap className="w-3.5 h-3.5" />
          Press {typeLabel}
        </Button>
      );
    }
    return (
      <DropdownMenu key={group.betType}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            className="h-9 px-3 text-sm font-bold border-0 gap-1"
            style={{ background: DT.interactive, color: DT.darkBg }}
            data-testid={`carousel-press-btn-${group.betType.toLowerCase()}`}
          >
            <Zap className="w-3.5 h-3.5" />
            Press {typeLabel}
            <ChevronDown className="w-3 h-3 ml-0.5 opacity-80" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" sideOffset={4}>
          {group.sides.map(({ side, holeOverride }) => {
            const teamIdx = side === 'top' ? 0 : 1;
            const team = gameState.teams[teamIdx];
            const teamName = team ? formatTeamName(team) : side;
            return (
              <DropdownMenuItem
                key={side}
                onClick={() => onRequestPress!(group.betType, side, 1, holeOverride)}
                data-testid={`carousel-press-opt-${group.betType.toLowerCase()}-${side}`}
              >
                {teamName} — Press {typeLabel}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Press controls sit in a dedicated row below the dollar amounts so they
  // never share layout space with the amounts (max 2 bet-type buttons visible
  // simultaneously: one of Front/Back + Overall).
  const pressControlRow = betGroups.length > 0 && onRequestPress ? (
    <div className="flex items-center justify-center gap-2 px-3 pb-2">
      {betGroups.map(renderPressControl)}
    </div>
  ) : null;

  if (isOnFront) {
    return (
      <div className="rounded-lg mt-2" style={{ background: DT.darkBg, border: `1px solid ${DT.divider}` }} data-testid="carousel-dollar-status">
        <div className="flex items-center justify-center gap-2 px-3 py-2">
          <span style={{ color: DT.primaryText, fontWeight: 800, fontSize: '18px', textTransform: 'uppercase' }}>Front</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '18px', color: dollarInlineColor(frontAmount) }} data-testid="dollar-front">
            {formatDollar(frontAmount)}
          </span>
        </div>
        {pressControlRow}
      </div>
    );
  }

  const activeAmount = backAmount + overallAmount;

  return (
    <div className="rounded-lg mt-2" style={{ background: DT.darkBg, border: `1px solid ${DT.divider}` }} data-testid="carousel-dollar-status">
      <div className="flex items-center justify-center gap-3 px-3 py-2">
        <div className="flex items-center gap-1.5">
          <Lock className="w-4 h-4" style={{ color: DT.primaryText }} />
          <span style={{ color: DT.primaryText, fontWeight: 800, fontSize: '18px', textTransform: 'uppercase' }}>Front</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '18px', color: dollarInlineColor(frontAmount) }} data-testid="dollar-front-locked">
            {formatDollar(frontAmount)}
          </span>
        </div>
        <span style={{ color: DT.divider, fontWeight: 700, fontSize: '18px' }}>|</span>
        <div className="flex items-center gap-1.5">
          <span style={{ color: DT.primaryText, fontWeight: 800, fontSize: '18px', textTransform: 'uppercase' }}>Active</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '18px', color: dollarInlineColor(activeAmount) }} data-testid="dollar-active">
            {formatDollar(activeAmount)}
          </span>
        </div>
      </div>
      {pressControlRow}
    </div>
  );
}

export function HoleDetailModal(props: HoleDetailModalProps) {
  const hole = props.gameState.holes.find(h => h.number === props.holeNumber);
  if (!hole) return null;
  return <HoleDetailModalInner {...props} hole={hole} />;
}

function HoleDetailModalInner({ gameState, holeNumber, hole, onClose, onBulkScoreChange, onHoleChange, onClearHole, onCorrectEarlierHole, onRequestPress, isCommish }: HoleDetailModalProps & { hole: HoleInfo }) {
  const allHoleNumbers = [...getFrontHoles(gameState.startingHole), ...getBackHoles(gameState.startingHole)];
  const currentIdx = allHoleNumbers.indexOf(holeNumber);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < allHoleNumbers.length - 1;
  const isLastHole = !hasNext;

  const totalPlayers = useMemo(() => getAllPlayers(gameState).length, [gameState.teams]);
  const initialDensity = getInitialDensity(totalPlayers);
  const { density, contentRef, containerRef } = useAdaptiveDensity(initialDensity);
  const cfg = densityConfig[density];

  const [localScores, setLocalScores] = useState<Record<string, number>>(() =>
    buildLocalScores(gameState, holeNumber, hole)
  );

  useEffect(() => {
    const h = gameState.holes.find(hh => hh.number === holeNumber);
    if (h) {
      setLocalScores(buildLocalScores(gameState, holeNumber, h));
    }
  }, [holeNumber]);

  const frontHoles = getFrontHoles(gameState.startingHole);
  const isInFront = frontHoles.includes(holeNumber);
  const legLabel = gameState.startingHole === 1
    ? (isInFront ? 'Front 9' : 'Back 9')
    : (isInFront ? 'Back 9' : 'Front 9');

  const holeHasExistingScore = gameState.scores.some(
    s => s.holeNumber === holeNumber && s.grossScore !== null && s.grossScore !== undefined
  );

  const [showClearConfirm, setShowClearConfirm] = useState(false);
  useEffect(() => { setShowClearConfirm(false); }, [holeNumber]);

  const commitScores = () => {
    onBulkScoreChange({ holeNumber, scores: { ...localScores } });
  };

  const PAUSE_HOLES = [8, 17];

  const handleSaveAndNext = () => {
    commitScores();
    if (!hasNext || PAUSE_HOLES.includes(holeNumber)) {
      onClose();
    } else {
      onHoleChange(allHoleNumbers[currentIdx + 1]);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="hole-detail-backdrop"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 pointer-events-none">
        <div
          ref={containerRef}
          className={cn(
            'pointer-events-auto w-[86vw] max-w-sm max-h-[70dvh] overflow-y-auto',
            'rounded-2xl shadow-2xl',
            'animate-in fade-in-0 zoom-in-95 duration-200'
          )}
          style={{ background: '#234A3A', border: `1px solid ${DT.divider}` }}
          onClick={e => e.stopPropagation()}
          data-testid="hole-detail-modal"
        >
         <div ref={contentRef}>
          <div className="sticky top-0 z-10 backdrop-blur-sm rounded-t-2xl" style={{ background: `${DT.surface}ee`, borderBottom: `1px solid ${DT.divider}` }}>
            <div className="flex items-center justify-center gap-2 px-3 py-2">
              <button
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full transition-colors"
                style={{ color: hasPrev ? DT.primaryText : DT.divider, cursor: hasPrev ? 'pointer' : 'default' }}
                onClick={() => hasPrev && onHoleChange(allHoleNumbers[currentIdx - 1])}
                disabled={!hasPrev}
                data-testid="modal-btn-prev-hole"
              >
                <ChevronLeft className="w-7 h-7" strokeWidth={1.5} />
              </button>
              <div className="text-center min-w-[140px]">
                <div style={{ fontSize: '16px', fontWeight: 800, color: holeNumber % 2 === 0 ? '#FACC15' : DT.primaryText }}>Hole {holeNumber}</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: DT.primaryText, opacity: 0.6 }}>
                  Par {hole.par} · {hole.yardage} yds · SI {hole.strokeIndex} · {legLabel}
                </div>
              </div>
              <button
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full transition-colors"
                style={{ color: hasNext ? DT.primaryText : DT.divider, cursor: hasNext ? 'pointer' : 'default' }}
                onClick={() => hasNext && onHoleChange(allHoleNumbers[currentIdx + 1])}
                disabled={!hasNext}
                data-testid="modal-btn-next-hole"
              >
                <ChevronRight className="w-7 h-7" strokeWidth={1.5} />
              </button>
            </div>
            <button
              onClick={onClose}
              className="absolute top-2 right-2 min-w-[36px] min-h-[36px] flex items-center justify-center rounded-full transition-colors"
              style={{ color: DT.primaryText }}
              data-testid="modal-btn-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className={cfg.bodyPadding}>
            <div className={cfg.teamGap}>
              {gameState.teams.map((team, teamIdx) => (
                <div key={team.id} className={cfg.rowGap}>
                  {gameState.teams.length > 1 && (
                    <div className="uppercase tracking-wider" style={{ color: DT.gold, fontWeight: 800, fontSize: '14px' }}>
                      {formatTeamName(team)}
                    </div>
                  )}
                  {team.players.map((player) => (
                    <ScoreInputRow
                      key={player.id}
                      player={player}
                      hole={hole}
                      displayScore={localScores[player.id] ?? hole.par}
                      onLocalChange={(val) => setLocalScores(prev => ({ ...prev, [player.id]: val }))}
                      isTeam1={teamIdx === 0}
                      isFinalized={gameState.isFinalized}
                      density={density}
                    />
                  ))}
                </div>
              ))}
            </div>

            <CarouselDollarStatus gameState={gameState} holeNumber={holeNumber} onRequestPress={onRequestPress} isCommish={isCommish} />

            {!gameState.isFinalized && (
              <div className="space-y-2 mt-2">
                <Button
                  className="w-full min-h-[46px] text-base font-bold border-0"
                  style={{
                    background: '#2F7A5F',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    borderRadius: '12px',
                    fontWeight: 600,
                  }}
                  onClick={handleSaveAndNext}
                  data-testid="modal-btn-save-next"
                >
                  {isLastHole || PAUSE_HOLES.includes(holeNumber) ? 'Save & Review' : 'Save & Next'}
                </Button>
                {onCorrectEarlierHole && (
                  <Button
                    variant="ghost"
                    className="w-full min-h-[38px] px-3 font-medium"
                    style={{ color: DT.primaryText, opacity: 0.65 }}
                    onClick={onCorrectEarlierHole}
                    data-testid="modal-btn-correct-earlier"
                    title="Correct Earlier Holes"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-2" />
                    Correct Earlier Holes
                  </Button>
                )}

                {holeHasExistingScore && onClearHole && (
                  <div>
                    {!showClearConfirm ? (
                      <button
                        type="button"
                        className="w-full flex items-center justify-center gap-1.5 text-xs transition-colors py-1"
                        style={{ color: DT.primaryText, opacity: 0.5 }}
                        onClick={() => setShowClearConfirm(true)}
                        data-testid="modal-btn-clear-score"
                      >
                        <Eraser className="w-3.5 h-3.5" />
                        Clear This Hole
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 rounded-lg p-2" style={{ background: DT.loseBg, border: `1px solid ${DT.losing}33` }}>
                        <span className="flex-1" style={{ fontSize: '12px', color: DT.losing }}>Clear score for Hole {holeNumber}? Presses starting here or later will be removed.</span>
                        <button
                          type="button"
                          className="px-2 py-1 rounded transition-colors"
                          style={{ fontSize: '12px', fontWeight: 600, color: DT.losing, background: `${DT.losing}22` }}
                          onClick={() => { onClearHole(holeNumber); setShowClearConfirm(false); }}
                          data-testid="modal-btn-confirm-clear"
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1"
                          style={{ fontSize: '12px', color: DT.primaryText, opacity: 0.5 }}
                          onClick={() => setShowClearConfirm(false)}
                          data-testid="modal-btn-cancel-clear"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
         </div>
        </div>
      </div>
    </>
  );
}
